/**
 * **One control surface, owned by the view.**
 *
 * Every dashboard page answers the same three-part question — *which dataset,
 * which channels, which period* — and until 8 Sep those three parts were seven
 * controls in six idioms across three places, because each arrived in a
 * different phase: a channel dropdown and two date inputs in the shell, dataset
 * tabs on the page, language-group and window pills bolted above them, and
 * search plus a duplicate channel select inside the table card.
 *
 * Two of those were genuine duplicates. **Channel was filterable twice**, and
 * **English was expressible twice** — as a group pill, or by ticking those three
 * channels. And **the period was split across two rows**: "Snap lifetime" is a
 * period, yet it sat away from the dates and had to reach back and grey them out
 * through a context that existed only for that purpose.
 *
 * So the controls live where the thing they scope lives, in reading order:
 *
 *   1 · **dataset** — the page's primary switch, directly under the title.
 *   2 · **slice** — scope and period, two self-describing buttons.
 *   3 · lookup — inside the table card, row-level only (`TableToolbar`).
 *
 * A control that needs a label to say what it is has not been named properly, so
 * there is no "Language group:" or "Window:" prefix here: the buttons read
 * *English · 3 channels* and *Last 30 days*.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { Hint } from '../components/Hint'
import { InfoDot } from '../components/InfoDot'
import { Tabs, type Tab } from '../components/Tabs'
import { api, type Channel } from '../lib/api'
import { formatRange } from '../lib/dates'
import { PERIOD_LABELS, PERIOD_NOTES, PERIODS, type Scope, useFilters } from '../lib/filters'

/** The channel registry, shared by every header. It changes by migration, not
 *  at runtime, so one query serves the whole session. */
function useChannelRegistry(): Channel[] {
  const query = useQuery({
    queryKey: ['channels'],
    queryFn: api.channels,
    staleTime: 60 * 60 * 1000,
  })
  return query.data ?? []
}

const control = {
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
}

const panel = {
  position: 'absolute' as const,
  top: 'calc(100% + 0.25rem)',
  left: 0,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-pop)',
  padding: '0.5rem',
  minWidth: '17rem',
  zIndex: 'var(--z-dropdown, 300)' as unknown as number,
}

/** Closes a popover on outside pointer-down and on Escape, and returns focus. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) close()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [close, open])
  return ref
}

// ---------------------------------------------------------------------------
// Scope — the language groups folded into the channel picker
// ---------------------------------------------------------------------------

function ScopeControl({ fallback }: { fallback: Scope }) {
  const registry = useChannelRegistry()
  const { channels, group, setChannels, setGroup } = useFilters()
  const [open, setOpen] = useState(false)
  const ref = useDismiss(open, () => setOpen(false))

  // Absence means "this page's default", which is English on the asset pages
  // and the whole estate on Channel Overview.
  const scope: Scope = group ?? fallback
  const counts: Record<Scope, number> = {
    english: registry.filter((channel) => channel.language_group === 'english').length,
    international: registry.filter((channel) => channel.language_group === 'international').length,
    all: registry.length,
  }
  const selected = channels.length
    ? channels
    : scope === 'all'
      ? registry.map((channel) => channel.handle)
      : registry.filter((channel) => channel.language_group === scope).map((c) => c.handle)

  const LABELS: Record<Scope, string> = {
    english: 'English',
    international: 'International',
    all: 'All Channels',
  }
  const label = channels.length
    ? `${channels.length} of ${registry.length || 13} channels`
    : scope === 'all'
      ? `All ${registry.length || 13} channels`
      : `${LABELS[scope]} · ${counts[scope]} channels`

  const option = (value: Scope) => {
    const active = !channels.length && scope === value
    return (
      <button
        key={value}
        type="button"
        onClick={() => {
          setGroup(value)
          setOpen(false)
        }}
        aria-pressed={active}
        className="block w-full px-2 py-1.5 text-left text-sm"
        style={{
          background: active ? 'var(--brand-tint)' : 'transparent',
          color: active ? 'var(--brand-ink)' : 'var(--text-primary)',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          fontWeight: active ? 600 : 400,
        }}
      >
        {LABELS[value]} <span style={{ color: 'var(--text-label)' }}>· {counts[value]}</span>
      </button>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="px-2.5 py-1.5 text-sm"
        style={control}
      >
        {label}
        <span aria-hidden="true" style={{ color: 'var(--text-label)' }}>
          {' '}
          ▾
        </span>
      </button>

      {open && (
        <div style={panel} role="group" aria-label="Channels">
          {/* The two halves of the business first, because that is how the
              client's own spec is organised — English is exactly three channels
              and International is the other ten. Then the individual list, for
              anyone who wants one show. */}
          {option('english')}
          {option('international')}
          {option('all')}

          <div
            className="mt-2 pt-2"
            style={{ borderTop: '1px solid var(--border)', maxHeight: '15rem', overflowY: 'auto' }}
          >
            {registry.map((channel) => (
              <label key={channel.handle} className="flex items-center gap-2 py-0.5 text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(channel.handle)}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? [...selected, channel.handle]
                      : selected.filter((handle) => handle !== channel.handle)
                    // Everything selected is expressed as no filter, so the URL
                    // stays short and shareable.
                    setChannels(next.length === registry.length ? [] : next)
                  }}
                />
                <span style={{ color: 'var(--text-secondary)' }}>{channel.display_label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Period — the named windows, Custom, and Snap lifetime, in one control
// ---------------------------------------------------------------------------

function PeriodControl({
  allowLifetime,
  shownTo,
}: {
  allowLifetime: boolean
  shownTo?: string
}) {
  const { period, from, to, setPeriod, setRange } = useFilters()
  // **The read-out says what is on screen, not what was asked for.**
  //
  // RV-1. Abi, 15 Sep: *"the default is on last 30 days and the range is showing
  // 8/15/2026 – 9/13/2026 but the data is showing the row for 9/14/2026 as
  // well."* She is reading a real contradiction: `/revenue` deliberately widens
  // its window to include today so today's figure can be typed, and the header
  // went on reporting the selected period. A header that disagrees with the
  // table beside it is worse than either number alone.
  const effectiveTo = shownTo && shownTo > to ? shownTo : to
  const [open, setOpen] = useState(false)
  const ref = useDismiss(open, () => setOpen(false))

  const options = PERIODS.filter((value) => allowLifetime || value !== 'lifetime')

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative" ref={ref}>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="px-2.5 py-1.5 text-sm"
          style={control}
        >
          {PERIOD_LABELS[period]}
          <span aria-hidden="true" style={{ color: 'var(--text-label)' }}>
            {' '}
            ▾
          </span>
        </button>

        {open && (
          <div style={{ ...panel, minWidth: '14rem' }}>
            {options.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setPeriod(value)
                  setOpen(false)
                }}
                aria-pressed={period === value}
                className="block w-full px-2 py-1.5 text-left text-sm"
                style={{
                  background: period === value ? 'var(--brand-tint)' : 'transparent',
                  color: period === value ? 'var(--brand-ink)' : 'var(--text-primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: period === value ? 600 : 400,
                  // Snap lifetime is a different kind of answer from the four
                  // date windows, so it is separated rather than listed as a
                  // fifth date range.
                  borderTop: value === 'lifetime' ? '1px solid var(--border)' : undefined,
                  marginTop: value === 'lifetime' ? '0.25rem' : undefined,
                  paddingTop: value === 'lifetime' ? '0.5rem' : undefined,
                }}
              >
                {PERIOD_LABELS[value]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* The dates appear only where they mean something: two inputs for a
          custom range, a plain read-out for a named one, and **nothing at all**
          on Snap lifetime — which is Snap's own windowless total, so a disabled
          date input there would be a control pretending to be relevant. */}
      {period === 'custom' ? (
        <span className="flex flex-wrap items-center gap-2 text-sm">
          <input
            type="date"
            value={from}
            max={to}
            aria-label="From"
            onChange={(event) => setRange(event.target.value, to)}
            className="px-2 py-1"
            style={{ ...control, cursor: 'auto' }}
          />
          <span style={{ color: 'var(--text-label)' }}>to</span>
          <input
            type="date"
            value={to}
            min={from}
            aria-label="To"
            onChange={(event) => setRange(from, event.target.value)}
            className="px-2 py-1"
            style={{ ...control, cursor: 'auto' }}
          />
        </span>
      ) : period === 'lifetime' ? (
        <Hint label={PERIOD_NOTES.lifetime}>
          <span className="text-sm" style={{ color: 'var(--text-label)', cursor: 'help' }}>
            No Date Window
          </span>
        </Hint>
      ) : (
        <span className="text-sm is-num" style={{ color: 'var(--text-label)' }}>
          {formatRange(from, effectiveTo)}
          {effectiveTo !== to && (
            <InfoDot note="Today Is Included So You Can Enter It. Every Other View Ends Yesterday, Because Snapchat's Day Is UTC and Today Is Still Counting." />
          )}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

export function ViewHeader<K extends string>({
  title,
  status,
  views,
  activeView,
  onView,
  allowLifetime = false,
  defaultScope = 'all',
  scoped = true,
  channelPicker = true,
  reserveTabRow = false,
  shownTo,
  children,
}: {
  title: string
  /** One line about what is on screen. Not a subtitle for decoration — it says
   *  what the numbers cover, which the controls alone cannot. */
  status?: ReactNode
  /** The dataset switch. Omitted where the page has one dataset. */
  views?: readonly Tab<K>[]
  activeView?: K
  onView?: (key: K) => void
  /** The scope to show when the URL carries none — English on the two asset
   *  pages, since that is the client spec's own first four tables. */
  defaultScope?: Scope
  /** Whether Snap lifetime is a period this view can answer. Channel-level
   *  daily rows cannot, so it is not offered there. */
  allowLifetime?: boolean
  /** False on pages the slice does not apply to at all — user management, say. */
  scoped?: boolean
  /**
   * False where *which channels* is not a question the view can answer.
   *
   * The revenue **entry grid** is the case: it is a form, not a report. You type
   * a day's payouts across the estate, and hiding columns there risks someone
   * believing a channel is absent — the same failure the asset pages avoid by
   * rendering unavailable columns present-and-explained rather than dropping
   * them. Its History and Change record tabs *are* reports and keep the control,
   * which is also what `APPLICATION_SPEC.md` §5.1 table B asks for.
   *
   * The period stays either way: it decides which days exist to type into.
   */
  channelPicker?: boolean
  /**
   * **The last day actually on screen, where a page deliberately shows one the
   * period does not — RV-1.**
   *
   * `/revenue` is the only case and it is not a bug: named periods end
   * *yesterday* because Snap's day is UTC and today is still accumulating, which
   * is right for a measured metric and wrong for a hand-typed one — today's
   * figure would otherwise be unreachable without switching to a custom range.
   *
   * What *was* a bug is the header going on reporting the selected period while
   * the grid, the history and the change record all rendered the extra day.
   * Passing it here fixes all three at once, which is why this is a header prop
   * rather than a caption inside the entry grid.
   */
  shownTo?: string
  /**
   * **Reserve the height of a tab row this page does not have — X-12.**
   *
   * Dishant's ask was that *KPI cards start at the same height on different
   * pages*, and the pages with KPI cards are the three dashboards: Stories,
   * Spotlights and Channel Overview. Only Stories has a dataset tab row, so the
   * other two need its height reserved or their tiles sit 50px higher.
   *
   * 🔴 **Opt-in, because the first version was not.** Applying the floor to
   * every header padded `/connections`, `/legacy`, `/connections/matches` and
   * `/admin/users` as well — pages with no tab row *and* no KPI row, so there
   * was nothing to line up and the space was simply dead. A page that already
   * has tabs gets the height naturally and passes nothing.
   */
  reserveTabRow?: boolean
  children?: ReactNode
}) {
  return (
    <header
      className="mb-5 flex flex-col"
      style={{
        /**
         * **X-12 / E-7 — one start line for every page's content.**
         *
         * Dishant, 11 Sep: *"KPI Cards should start at same height on different
         * pages… Tables should start at same."* Measured before changing
         * anything: `/stories` carried a 123px header and `/channels` and
         * `/spotlights` 72px, because only the first has a dataset tab row. So
         * the KPI row on one page sat 51px lower than the tiles on another, and
         * flicking between them moved the numbers under the reader's eye.
         *
         * The tallest arrangement — title, tab row, slice controls — sets the
         * floor, and a page without tabs leaves that space empty. It is
         * `min-height`, not `height`, so a wrapping title still grows the header
         * rather than colliding with the content under it.
         *
         * 🔴 **`flex-start`, and the first attempt got this wrong.** With
         * `flex-end` the reserved space landed *above* the title, so every page
         * without a tab row opened on a band of empty white with the heading
         * floating halfway down it — a worse problem than the one being fixed,
         * and Nilay spotted it on `/spotlights` immediately. Top-aligned, the
         * slack falls *below* the controls instead, where it reads as breathing
         * room, and the content still starts on the same line.
         */
        minHeight: views || reserveTabRow ? '7.75rem' : undefined,
        justifyContent: 'flex-start',
      }}
    >
      <div className="mb-3 flex flex-wrap items-baseline gap-3">
        <h1 className="m-0 text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </h1>
        {status && (
          <p className="m-0 text-xs" style={{ color: 'var(--text-label)' }}>
            {status}
          </p>
        )}
      </div>

      {views && activeView && onView && (
        <Tabs tabs={views} active={activeView} onChange={onView} label={`${title} dataset`} />
      )}

      {scoped && (
        <div
          className="flex flex-wrap items-center gap-2 pb-1"
          // No card, no fill: the slice is a row of controls, not a panel. A
          // surface here would compete with the tables, which are the content.
        >
          {channelPicker && <ScopeControl fallback={defaultScope} />}
          <PeriodControl allowLifetime={allowLifetime} shownTo={shownTo} />
          {children}
        </div>
      )}
    </header>
  )
}
